import React, { useState } from 'react';
import SalvPage from '../common/SalvPage';
import { useQuery } from '@tanstack/react-query';
import { scenarioLibraryInternalApi } from '../apis';
import { useLocation, useNavigate, useParams } from 'react-router';
import { SalvButton, Size } from '@salvapp/ui-components';
import './MonitoringScenario.scss';
import { FieldView } from './FieldEditor';
import { monitoringScenarioTypes } from './MonitoringScenarios';
import ImportLibraryScenarioModal from './ImportLibraryScenarioModal';
import LibraryScenarioVersions from './versions/LibraryScenarioVersions';
import { CAN_ADD_SCENARIO } from '../auth/permissions';
import { useMe } from '../organisation/useMe';

const LibraryScenario = () => {
  const { handle } = useParams() as { handle: string };
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const { data: libraryScenario } = useQuery({
    queryKey: ['library_scenario', handle],
    queryFn: async () => await scenarioLibraryInternalApi.getLibraryScenario({ scenarioHandle: handle }),
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = useMe();
  const canAddScenario = permissions.includes(CAN_ADD_SCENARIO);

  const importScenario = async (scenarioId: string) => {
    navigate(`/scenarios/${scenarioId}`, { state: { ...location.state, search: 'tab=versions' } });
  };

  return (
    libraryScenario && (
      <SalvPage
        title={`Salv scenario: ${libraryScenario?.name}`}
        breadcrumb={{
          title: 'Scenario list',
          to: { pathname: '/scenarios', search: location.state?.search },
        }}
      >
        {libraryScenario && (
          <div className='scenario-status-section d-flex align-items-center'>
            <div className='text-secondary'>
              This is a Salv library scenario. Add it to your scenarios to modify, test and activate it.
            </div>
            <SalvButton
              variant='primary'
              size={Size.EXTRA_SMALL}
              label='Add to my scenarios'
              className='m-l-8'
              onClick={() => setShowDuplicateModal(true)}
              disabled={!canAddScenario}
            />
          </div>
        )}

        <div className='scenario-details-section'>
          <div className='details-column-with-scenario-name'>
            <FieldView label='Internal ID' value={libraryScenario.internalId} />
            <FieldView label='Scenario name' value={libraryScenario.name} />
          </div>
          <div>
            <div>
              <FieldView
                label='Category'
                value={libraryScenario.categories.map((category) => category.name).join(', ')}
              />
            </div>

            <div>
              <FieldView
                label='Typology'
                value={libraryScenario.categories
                  .flatMap((category) => category.typologies.map((typology) => typology.name))
                  .join(', ')}
              />
            </div>
          </div>
        </div>

        <div className='d-flex'>
          <div className='scenario-card'>
            <div className='content'>
              <FieldView
                label='Scenario types'
                value={libraryScenario.type.map((type) => monitoringScenarioTypes[type]).join(', ')}
              />
              <FieldView
                label='Alerting entity'
                value={<span className='text-capitalize'>{libraryScenario.entityType.toLowerCase()}</span>}
              />
            </div>
          </div>
        </div>

        <div className='m-t-24'>
          <LibraryScenarioVersions libraryScenario={libraryScenario} />
        </div>
        <ImportLibraryScenarioModal
          showModal={showDuplicateModal}
          onClose={() => setShowDuplicateModal(false)}
          onCreate={importScenario}
          libraryScenario={libraryScenario}
        />
      </SalvPage>
    )
  );
};

export default LibraryScenario;
